import { explain, InputError } from './errors.ts';
import { esc, icon, type Money } from './format.ts';

/** DOM helpers shared by every view: the query shortcut, toasts, actions with a busy button, inputs. */

export const $ = <T extends HTMLElement>(sel: string) => document.querySelector(sel) as T;

type Toast = { done: (text: string) => void; fail: (e: unknown) => void };

/**
 * Keeps room for the toasts at the bottom of the page (`--toasts-room`, the html's
 * scroll-padding-bottom), so the focused element scrolls above them instead of under them (#234).
 */
const FOCUS_MARGIN = 8;
function roomForToasts() {
  const box = document.querySelector<HTMLElement>('#toasts');
  const room = box?.childElementCount
    ? Math.max(0, Math.ceil(window.innerHeight - box.getBoundingClientRect().top)) + FOCUS_MARGIN
    : 0;
  document.documentElement.style.setProperty('--toasts-room', `${room}px`);
}

/**
 * What goes under a toast's sentence: the API's code and message in sight, or folded under
 * «Detalles» when the sentence already says it in Spanish (#235: support still reaches it).
 */
const bodyHtml = (t: string, d?: string, folded?: boolean) =>
  `<div class="toast-body"><p>${esc(t)}${d && !folded ? `<small>${esc(d)}</small>` : ''}</p>${
    d && folded
      ? `<details class="toast-detail"><summary>Detalles</summary><code>${esc(d)}</code></details>`
      : ''
  }</div>`;

/** Short confirmations and errors, announced by the aria-live region they live in. */
export function toast(
  text: string,
  kind: 'ok' | 'error' | 'busy' = 'ok',
  detail?: string,
  folded?: boolean,
): Toast {
  const el = document.createElement('div');
  const paint = (t: string, k: typeof kind, d?: string, f?: boolean) => {
    el.className = `toast ${k}`;
    el.setAttribute('role', k === 'error' ? 'alert' : 'status');
    el.innerHTML = `${icon(k === 'error' ? 'alert' : k === 'busy' ? 'spin' : 'ok')}${bodyHtml(t, d, f)}`;
    // It goes after a while, unless someone is reading its «Detalles».
    const expire = () => {
      if (el.querySelector('details[open]')) return void setTimeout(expire, 3000);
      el.remove();
      roomForToasts();
    };
    if (k !== 'busy') setTimeout(expire, k === 'error' ? 9000 : 4500);
    roomForToasts();
  };
  paint(text, kind, detail, folded);
  $('#toasts').append(el);
  roomForToasts();
  return {
    done: (t) => paint(t, 'ok'),
    fail: (e) => {
      const { human, detail: d, translated } = explain(e);
      paint(human, 'error', d, translated);
    },
  };
}

/** "Q1,250.50" typed by a person → minor units (throws on anything that is not an amount). */
export const qInput = (sel: string): Money => {
  const raw = ($(sel) as HTMLInputElement).value.replace(/[Q,\s]/gi, '');
  const n = Number(raw);
  if (!raw || !Number.isFinite(n) || n < 0)
    throw new InputError('Escribe un monto válido, por ejemplo 500.00');
  return { amountMinor: Math.round(n * 100), currency: 'GTQ' };
};
/**
 * "−1,250.5" typed by a person → exact centavos (no floating point): null when empty; throws
 * when it is not an amount with at most two decimals (or is negative and `signed` is false).
 */
export const centsOf = (text: string, signed: boolean): number | null => {
  const raw = text.replace(/[Q$,\s]|US/gi, '').replace('−', '-');
  if (!raw) return null;
  const m = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(raw);
  if (!m || (m[1] && !signed))
    throw new InputError(
      signed
        ? 'Escribe un monto válido, por ejemplo 112.00 (negativo si la aseguradora devuelve prima)'
        : 'Escribe un monto válido, por ejemplo 18.25',
    );
  const cents = Number(m[2]) * 100 + Number((m[3] ?? '').padEnd(2, '0'));
  return m[1] ? -cents : cents;
};
export const textInput = (sel: string, what: string) => {
  const v = ($(sel) as HTMLInputElement).value.trim();
  if (!v) throw new InputError(`Falta ${what}.`, sel.startsWith('#') ? sel.slice(1) : undefined);
  return v;
};

/**
 * Runs one action with its button busy and a toast that reports the outcome. The busy button is
 * also disabled (#242): `aria-busy` alone stops the mouse (the CSS), not a second Enter of the
 * keyboard. A disabled button loses the focus, so it gets it back at the end, unless the action
 * put it somewhere else (the new policy, #234; a field with an error) or painted the screen again.
 */
export async function act(button: HTMLElement | null, busy: string, run: () => Promise<string>) {
  const control = button && 'disabled' in button ? (button as HTMLButtonElement) : null;
  const wasDisabled = control?.disabled ?? false;
  const hadFocus = !!button && document.activeElement === button;
  button?.setAttribute('aria-busy', 'true');
  if (control) control.disabled = true;
  const t = toast(busy, 'busy');
  try {
    t.done(await run());
  } catch (e) {
    t.fail(e);
  } finally {
    button?.removeAttribute('aria-busy');
    // Only what `act` did is undone: a control its caller disabled (a reminder's switch) is the
    // caller's to enable again.
    if (control && !wasDisabled) control.disabled = false;
    const lost = document.activeElement === null || document.activeElement === document.body;
    if (
      button &&
      hadFocus &&
      !control?.disabled &&
      button.isConnected &&
      (lost || document.activeElement === button)
    )
      button.focus({ preventScroll: true });
  }
}
